import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Magazinul de servicii - capetele lui `controllers/Service_shop.php`.
 *
 * CITIRILE erau singurele de aici: `add`, `setQuantity` si `removeItem`
 * ramasesera, la mutare, in portalul vechi. Portalul acela a fost scos de pe
 * server, deci au venit incoace - vezi `EaseedeskServiceShopController`.
 *
 * `submit` a venit si el: trimiterea naste tichetele cererii.
 *
 * `apiGet` si `apiPost` pun singure `/api/v2` in fata caii - de-aia caile de
 * aici incep de la `/easeedesk`.
 */

export interface ShopOffer {
  ID: number
  ci_id: number
  name: string
  class_name: string
  category_id: number
  category_name: string
  icon: string
  image_path: string
  /** Adresa publica a imaginii (bucketul `public_images`), '' fara imagine. */
  image_url: string
  short_description: string
  long_description: string
  requires_approval: boolean
  request_ticket_type_id: number
  commercial_template_id: number
  /** `null` inseamna "fara sablon comercial", nu "gratis". */
  estimate_unit_cost: number | null
  currency: string
  sla_name: string
}

export interface ShopCategory {
  id: number
  name: string
  total: number
}

/** Un stadiu din fluxul tipului de tichet - `Workflow_steps::stagesForTicketType`. */
export interface ShopStage {
  name: string
  colour: string | null
  type: 'active' | 'resolved' | 'final' | string
  goes_to: string | null
}

export interface ShopPage {
  title: string
  offers: ShopOffer[]
  categories: ShopCategory[]
  filters: { search: string; category_id: number }
  cartCount: number
  /**
   * Are omul dreptul de ADMINISTRARE a catalogului
   * (`inventoree.service_catalog.read`)? De el atarna fila "Service Catalog"
   * din banda, ca in `_tabs.php` - alt drept decat cel al magazinului.
   */
  canCatalog: boolean
}

export interface ShopOfferPage {
  offer: ShopOffer
  stages: ShopStage[]
}

export interface ShopItem {
  ID: number
  catalog_id: number
  quantity: number
  status: number
  /** Cuvantul MAGAZINULUI. Se arata numai cat timp nu exista tichet. */
  state: string
  name_at_request: string
  current_name: string
  price_at_request: number | null
  currency_at_request: string
  decision_note: string
  icon: string
  short_description: string
  requires_approval: boolean
  approver_group_name: string
  ticket_id: number
  ticket_status_name: string
  ticket_status_color: string
  ticket_status_type: number
  ticket_assignee_name: string
  ticket_group_name: string
  ticket_updated: string
}

export interface ShopCart {
  ID: number
  status: number
  created_at: string
}

export interface ShopCartPage {
  /** `null` = omul n-are inca niciun cos. Citirea NU naste unul. */
  cart: ShopCart | null
  items: ShopItem[]
  people: { id: number; name: string }[]
  priorities: { ID: number; priority_name: string }[]
  cartCount: number
}

export interface ShopRequestRow {
  ID: number
  status: number
  submitted_at: string
  note: string
  items: number
  waiting: number
  pending: number
  running: number
  rejected: number
  ticket_updated: string
}

export interface ShopRequestsPage {
  title: string
  requests: ShopRequestRow[]
  cartCount: number
  /**
   * Are omul dreptul de ADMINISTRARE a catalogului
   * (`inventoree.service_catalog.read`)? De el atarna fila "Service Catalog"
   * din banda, ca in `_tabs.php` - alt drept decat cel al magazinului.
   */
  canCatalog: boolean
}

export interface ShopChildTicket {
  ID: number
  parent_ticket_id: number
  subject: string
  status_name: string
  status_color: string
  status_type: number
}

export interface ShopEvent {
  ID: number
  event: number
  word: string
  /** Gol inseamna "sistemul", nu "un om care a uitat sa fie inregistrat". */
  who: string
  detail: string
  created_at: string
}

export interface ShopRequestPage {
  request: {
    ID: number
    status: number
    submitted_at: string
    note: string
    requester_name: string
    company_name: string
  }
  items: ShopItem[]
  /** Tichetele copil, pe id de tichet parinte. */
  children: Record<string, ShopChildTicket[]>
  events: ShopEvent[]
}

/** `Service_shop::index()`. */
export function serviceShopQuery(search: string, category: number) {
  const p = new URLSearchParams()
  if (search !== '') p.set('search', search)
  if (category > 0) p.set('category', String(category))
  const query = p.toString()

  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', search, category],
    queryFn: () => apiGet<ShopPage>(`/easeedesk/service-shop${query === '' ? '' : `?${query}`}`),
  })
}

/** `Service_shop::details()`. */
export function serviceShopOfferQuery(offer: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', 'offer', offer],
    queryFn: () => apiGet<ShopOfferPage>(`/easeedesk/service-shop/offers/${offer}`),
    enabled: offer > 0,
  })
}

/** `Service_shop::cart()`, fara insertul ei - vezi `ServiceShop` in api. */
export function serviceShopCartQuery() {
  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', 'cart'],
    queryFn: () => apiGet<ShopCartPage>('/easeedesk/service-shop/cart'),
  })
}

/** `Service_shop::requests()`. */
export function serviceRequestsQuery() {
  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', 'requests'],
    queryFn: () => apiGet<ShopRequestsPage>('/easeedesk/service-shop/requests'),
  })
}

/** `Service_shop::request()`. */
export function serviceRequestQuery(request: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', 'requests', request],
    queryFn: () => apiGet<ShopRequestPage>(`/easeedesk/service-shop/requests/${request}`),
    enabled: request > 0,
  })
}

/* ===================================================================== *
 *                        SCRIERILE COSULUI
 * ===================================================================== */

/**
 * Raspunsul celor trei scrieri.
 *
 * `message` vine de la SERVER si se arata ca atare. Nu se scrie in ecran: doar
 * serverul stie daca randul a fost adaugat sau doar i-a crescut cantitatea, iar
 * cele doua locuri care si-l scriau singure in portal ajunsesera sa spuna
 * "undefined" pe fisa serviciului.
 */
export interface ShopWriteResult {
  status: string
  message: string
  item_id?: number
  merged?: boolean
  /** Numarul de langa cos, in BUCATI - nu in randuri. */
  cart_count: number
}

/** `Service_shop::add()` - "Add to cart", de pe card sau de pe fisa. */
export function addToCart(catalogId: number, quantity: number, note: string): Promise<ShopWriteResult> {
  return apiPost<ShopWriteResult>('/easeedesk/service-shop/cart/items', {
    catalog_id: catalogId,
    quantity,
    note,
  })
}

/** `Service_shop::setQuantity()`. */
export function setCartQuantity(itemId: number, quantity: number): Promise<ShopWriteResult> {
  return apiPost<ShopWriteResult>(`/easeedesk/service-shop/cart/items/${itemId}`, { quantity })
}

/** `Service_shop::removeItem()`. Un POST, ca in portal - vezi controllerul. */
export function removeCartItem(itemId: number): Promise<ShopWriteResult> {
  return apiPost<ShopWriteResult>(`/easeedesk/service-shop/cart/items/${itemId}/remove`, {})
}

/** Raspunsul trimiterii - `Service_shop::submit()`. */
export interface ShopSubmitResult extends ShopWriteResult {
  request_id: number
  /** Cate articole au plecat din cos. */
  sent: number
  /** Cate tichete s-au deschis, parinti si copii la un loc. */
  tickets: number
  /**
   * Ce N-A putut fi deschis, si de ce.
   *
   * In portal, motivele mergeau in `log_message('error', ...)`: omul apasa,
   * primea "Sent", si nu afla niciodata ca un serviciu din cererea lui n-a
   * produs niciun tichet. Aici se arata.
   */
  problems: string[]
}

/** `Service_shop::submit()`. Cosul se afla din sesiune; nu se trimite niciun id. */
export function submitCart(note: string, beneficiary: number, priority: number): Promise<ShopSubmitResult> {
  return apiPost<ShopSubmitResult>('/easeedesk/service-shop/cart/submit', {
    note,
    beneficiary,
    ticket_priority: priority,
  })
}

/** Cos pregatit de biroul AI, vizibil operatorului. */
export interface ShopPreparedCart {
  ID: number
  company_id: number
  company_name: string
  requester_user_id: number
  requester_name: string
  prepared_from_ticket: number
  ticket_subject: string
  note: string
  items: ShopItem[]
  created_at: string
}

export function preparedCartsQuery() {
  return queryOptions({
    queryKey: ['easeedesk', 'service-shop', 'prepared'],
    queryFn: () =>
      apiGet<{ rows: ShopPreparedCart[]; priorities: { ID: number; priority_name: string }[] }>(
        '/easeedesk/service-shop/prepared'
      ),
  })
}

export function submitPreparedCart(requestId: number, note: string, priority: number): Promise<ShopSubmitResult> {
  return apiPost<ShopSubmitResult>(`/easeedesk/service-shop/prepared/${requestId}/submit`, { note, priority })
}
